import React, { useState, useRef, useMemo } from 'react';
import api from '../../services/api';
import ModuloItem from '../../components/ModuloItem';

function BannerSucesso({ mensagem }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
      <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      <p className="text-sm text-green-700">{mensagem}</p>
    </div>
  );
}

function BannerErro({ mensagem }) {
  if (!mensagem) return null;
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
      <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p className="text-sm text-red-700">{mensagem}</p>
    </div>
  );
}

function contarRespondidos(modulo, valores) {
  const itens = modulo.itens || [];
  if (!itens.length) return { respondidos: 0, total: 0 };
  const respondidos = itens.filter(item => {
    const v = valores?.[item.id];
    if (item.tipo === 'faixa') return v?.de !== null && v?.de !== undefined && v?.ate !== null && v?.ate !== undefined;
    return v !== null && v !== undefined;
  }).length;
  return { respondidos, total: itens.length };
}

// ── Painel Geral ─────────────────────────────────────────────────────────────

function PainelModulosGeral({ modulo, valoresIniciais, onboardingId, readOnly, atualizarDados }) {
  const [valores, setValores] = useState(valoresIniciais || {});
  const [replicarEmpresas, setReplicarEmpresas] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState('');
  const [erro, setErro] = useState('');
  const timerSucesso = useRef(null);

  function alterarValor(itemId, novoValor) {
    setValores(prev => ({ ...prev, [itemId]: novoValor }));
  }

  async function handleSalvar(e) {
    e.preventDefault();
    if (readOnly) return;
    setSalvando(true);
    setSucesso('');
    setErro('');
    try {
      await api.put(`/onboardings/${onboardingId}/modulos`, {
        modId: modulo.id,
        valores,
        replicarEmpresas,
      });
      await atualizarDados();
      setSucesso(`Módulo "${modulo.nome}" salvo com sucesso.`);
      if (timerSucesso.current) clearTimeout(timerSucesso.current);
      timerSucesso.current = setTimeout(() => setSucesso(''), 4000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  const itens = modulo.itens || [];

  return (
    <form onSubmit={handleSalvar} className="space-y-2">
      {sucesso && <BannerSucesso mensagem={sucesso} />}
      {erro && <BannerErro mensagem={erro} />}

      {itens.length === 0 ? (
        <div className="card text-center py-6">
          <p className="text-sm text-gray-400">Este módulo não possui itens configuráveis.</p>
        </div>
      ) : (
        <div className="card space-y-1">
          {itens.map((item, idx) => (
            <ModuloItem
              key={item.id}
              item={item}
              valor={valores[item.id]}
              onChange={val => alterarValor(item.id, val)}
              readOnly={readOnly}
              index={idx}
            />
          ))}
        </div>
      )}

      {!readOnly && itens.length > 0 && (
        <div className="flex items-center justify-between pt-1">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={replicarEmpresas}
              onChange={e => setReplicarEmpresas(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-400"
            />
            Replicar para todas as empresas
          </label>
          <button type="submit" disabled={salvando} className="btn-primary">
            {salvando ? 'Salvando...' : `Salvar ${modulo.nome}`}
          </button>
        </div>
      )}
    </form>
  );
}

// ── Painel por Empresa ────────────────────────────────────────────────────────

function PainelModulosEmpresa({ modulo, valoresIniciais, onboardingId, empresa, readOnly, atualizarDados }) {
  const [valores, setValores] = useState(valoresIniciais || {});
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState('');
  const [erro, setErro] = useState('');
  const timerSucesso = useRef(null);

  function alterarValor(itemId, novoValor) {
    setValores(prev => ({ ...prev, [itemId]: novoValor }));
  }

  async function handleSalvar(e) {
    e.preventDefault();
    if (readOnly) return;
    setSalvando(true);
    setSucesso('');
    setErro('');
    try {
      await api.put(`/onboardings/${onboardingId}/empresas/${empresa.id}/modulos`, {
        modId: modulo.id,
        valores,
      });
      await atualizarDados();
      setSucesso(`Módulo "${modulo.nome}" salvo para ${empresa.nome}.`);
      if (timerSucesso.current) clearTimeout(timerSucesso.current);
      timerSucesso.current = setTimeout(() => setSucesso(''), 4000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  const itens = modulo.itens || [];

  return (
    <form onSubmit={handleSalvar} className="space-y-2">
      {sucesso && <BannerSucesso mensagem={sucesso} />}
      {erro && <BannerErro mensagem={erro} />}

      {itens.length === 0 ? (
        <div className="card text-center py-6">
          <p className="text-sm text-gray-400">Este módulo não possui itens configuráveis.</p>
        </div>
      ) : (
        <div className="card space-y-1">
          {itens.map((item, idx) => (
            <ModuloItem
              key={item.id}
              item={item}
              valor={valores[item.id]}
              onChange={val => alterarValor(item.id, val)}
              readOnly={readOnly}
              index={idx}
            />
          ))}
        </div>
      )}

      {!readOnly && itens.length > 0 && (
        <div className="flex justify-end pt-1">
          <button type="submit" disabled={salvando} className="btn-primary">
            {salvando ? 'Salvando...' : `Salvar ${modulo.nome}`}
          </button>
        </div>
      )}
    </form>
  );
}

// ── Pill de progresso na tab do módulo ────────────────────────────────────────

function PillProgresso({ respondidos, total }) {
  if (total === 0) return null;
  const completo = respondidos === total;
  return (
    <span className={`ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
      completo
        ? 'bg-green-100 text-green-700'
        : 'bg-amber-100 text-amber-700'
    }`}>
      {respondidos}/{total}
    </span>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function Modulos({ dados, modulos, onboardingId, atualizarDados }) {
  const readOnly = !!dados.finalizado;
  const empresas = dados.empresas || [];

  const modulosVisiveis = useMemo(() => (
    (dados.modulosContratados || [])
      .map(modId => modulos.find(m => m.id === modId))
      .filter(Boolean)
  ), [dados.modulosContratados, modulos]);

  const [moduloAtivoId, setModuloAtivoId] = useState(() => modulosVisiveis[0]?.id || null);
  const [contextAtivo, setContextAtivo] = useState('geral');

  const moduloAtivo = modulosVisiveis.find(m => m.id === moduloAtivoId) || modulosVisiveis[0] || null;

  const contextos = useMemo(() => [
    { id: 'geral', label: 'Configuração Geral' },
    ...empresas.map(emp => ({ id: `empresa-${emp.id}`, label: emp.nome || `Empresa`, empresa: emp })),
  ], [empresas]);

  const empresaAtiva = contextos.find(c => c.id === contextAtivo)?.empresa || null;

  if (modulosVisiveis.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="section-title">Módulos</h1>
          <p className="section-sub">Configure os módulos contratados pela sua empresa.</p>
        </div>
        <div className="card text-center py-12 space-y-3">
          <div className="text-4xl">⚙️</div>
          <p className="font-semibold text-gray-700">Nenhum módulo configurado</p>
          <p className="text-sm text-gray-400 max-w-sm mx-auto">
            Nenhum módulo foi contratado ou configurado ainda.
            Entre em contato com a HeadThink para mais informações.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="section-title">Módulos</h1>
        <p className="section-sub">Configure os módulos contratados pela sua empresa.</p>
      </div>

      {/* ── Tabs de módulos (nível 1) ── */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex overflow-x-auto border-b border-gray-200">
          {modulosVisiveis.map(mod => {
            const valores = contextAtivo === 'geral'
              ? dados.modulosValoresPrincipais?.[mod.id]
              : empresas.find(e => `empresa-${e.id}` === contextAtivo)?.modulosValores?.[mod.id];
            const { respondidos, total } = contarRespondidos(mod, valores);
            const ativo = mod.id === moduloAtivo?.id;
            return (
              <button
                key={mod.id}
                type="button"
                onClick={() => setModuloAtivoId(mod.id)}
                className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors shrink-0 ${
                  ativo
                    ? 'border-brand-600 text-brand-700 bg-brand-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {mod.nome}
                <PillProgresso respondidos={respondidos} total={total} />
              </button>
            );
          })}
        </div>

        {/* ── Tabs de contexto (nível 2) — só se houver empresas ── */}
        {contextos.length > 1 && (
          <div className="flex overflow-x-auto border-b border-gray-100 bg-gray-50 px-2 gap-1 py-1.5">
            {contextos.map(ctx => (
              <button
                key={ctx.id}
                type="button"
                onClick={() => setContextAtivo(ctx.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                  contextAtivo === ctx.id
                    ? 'bg-white text-brand-700 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white'
                }`}
              >
                {ctx.label}
              </button>
            ))}
          </div>
        )}

        {/* ── Conteúdo ── */}
        <div className="p-3">
          {moduloAtivo && contextAtivo === 'geral' && (
            <PainelModulosGeral
              key={`${moduloAtivo.id}-geral`}
              modulo={moduloAtivo}
              valoresIniciais={dados.modulosValoresPrincipais?.[moduloAtivo.id] || {}}
              onboardingId={onboardingId}
              readOnly={readOnly}
              atualizarDados={atualizarDados}
            />
          )}

          {moduloAtivo && empresaAtiva && (
            <PainelModulosEmpresa
              key={`${moduloAtivo.id}-${empresaAtiva.id}`}
              modulo={moduloAtivo}
              valoresIniciais={
                empresas.find(e => e.id === empresaAtiva.id)?.modulosValores?.[moduloAtivo.id] || {}
              }
              onboardingId={onboardingId}
              empresa={empresaAtiva}
              readOnly={readOnly}
              atualizarDados={atualizarDados}
            />
          )}
        </div>
      </div>
    </div>
  );
}
